import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import type { SolveCatalog } from "@playbookai/ui";
import { useAuthStore } from "../auth/auth-store.js";
import {
  getSolveCatalogItem,
  listSolveCatalogPage,
} from "../api/solve-catalog-api.js";

/**
 * Lazy SOLVE picker catalogue for the SOLVE Actions editor, plus the owner
 * VOCABULARY that editor offers.
 *
 * The owners come from the resolved widget data rather than from a directory
 * read, because they are free text typed into two canvas widgets and there is
 * no endpoint that lists them. The host passes what the currently SAVED scope
 * resolved to; the editor merges the saved selection on top so a choice made
 * under a wider scope is never dropped. Widening the scope in the DRAFT does
 * not widen the options until the change is saved — stated in the editor's own
 * hint rather than papered over.
 */
export function useSolveCatalog(owners: readonly string[]): SolveCatalog {
  const config = useApiClient();
  const claims = useAuthStore((state) => state.claims);
  const authed = claims?.tenantId != null && claims?.userId != null;
  const [active, setActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const activate = useCallback((ids: readonly string[]) => {
    setActive(true);
    setSelectedIds((previous) => {
      const next = [...new Set(ids)];
      return previous.length === next.length &&
        previous.every((id, index) => id === next[index])
        ? previous
        : next;
    });
  }, []);
  const query = useInfiniteQuery({
    queryKey: [
      "solve-catalog",
      claims?.tenantId ?? "none",
      claims?.userId ?? "anon",
      searchQuery.trim(),
    ],
    enabled: active && authed,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listSolveCatalogPage(config, {
        ...(searchQuery.trim() ? { q: searchQuery.trim() } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 20,
        signal,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 60_000,
    retry: false,
  });
  // A saved selection may sit beyond the first page (or outside the current
  // search), so each selected id is resolved on its own and merged in front.
  const selectedQueries = useQueries({
    queries: selectedIds.map((id) => ({
      queryKey: [
        "solve-catalog-item",
        claims?.tenantId ?? "none",
        claims?.userId ?? "anon",
        id,
      ],
      queryFn: () => getSolveCatalogItem(config, id),
      enabled: active && authed,
      staleTime: 60_000,
      retry: false,
    })),
  });
  const selectedItems = selectedQueries.flatMap((item) =>
    item.data ? [item.data] : [],
  );
  const solves = useMemo(() => {
    const rows = [
      ...selectedItems,
      ...(query.data?.pages.flatMap((page) => page.items) ?? []),
    ];
    return [...new Map(rows.map((row) => [row.id, row])).values()];
  }, [query.data, selectedItems]);
  const selectionError = selectedQueries.some((item) => item.isError);
  return {
    solves,
    owners,
    isLoading:
      (query.isPending && query.isFetching) ||
      selectedQueries.some((item) => item.isPending && item.isFetching),
    isLoadingMore: query.isFetchingNextPage,
    error:
      query.error || selectionError
        ? "SOLVE choices couldn’t be loaded."
        : null,
    hasMore: query.hasNextPage,
    searchQuery,
    activate,
    onSearchChange: setSearchQuery,
    onLoadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage)
        void query.fetchNextPage();
    },
    onRetry: () => {
      void query.refetch();
      for (const selectedQuery of selectedQueries) {
        if (selectedQuery.isError) void selectedQuery.refetch();
      }
    },
  };
}
